import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, firestore, query, where, getDocs, setDoc } from '../../firebase';
import { collection, doc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import ReCaptcha from './ReCaptcha';

const NewUserSignIn = () => {
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showErrorMessage = (message) => {
    setSuccessMessage('');
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage('');
    }, 2000);
  };

  const showSuccessMessage = (message) => {
    setErrorMessage('');
    setSuccessMessage(message);
  };

  const handleReCaptchaVerify = (verified) => {
    setIsVerified(verified);
    if (verified) {
      showErrorMessage('');
    } else {
      showErrorMessage('ReCaptcha verification failed. Please try again.');
    }
  };

  const SignInWithGoogle = async () => {
    if (!isVerified) {
      showErrorMessage('Please complete the ReCaptcha verification.');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      let userRole = 'customer';

      // Check if the user exists in UserProfileMaster
      const userProfileQuery = query(
        collection(firestore, 'UserProfileMaster'),
        where('email', '==', user.email)
      );
      const userProfileSnapshot = await getDocs(userProfileQuery);

      if (!userProfileSnapshot.empty) {
        // Existing user
        const userProfileData = userProfileSnapshot.docs[0].data();
        userRole = userProfileData.role || 'customer';

        // Save role and navigate to HomePage
        sessionStorage.setItem('userRole', userRole);
        if (userProfileData.tenantId || userProfileData.tenantid) {
          sessionStorage.setItem('tenantId', userProfileData.tenantId || userProfileData.tenantid);
        } else {
          sessionStorage.removeItem('tenantId');
        }
        navigate('/HomePage');
        return;
      }

      // New registration from this screen always creates a customer profile.
      await setDoc(doc(firestore, 'UserProfileMaster', user.uid), {
        uid: user.uid,
        displayName: user.displayName || '',
        email: user.email,
        phoneNumber: user.phoneNumber || '',
        photoURL: user.photoURL,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user.uid,
        role: userRole,
        tenantId: null,
      });

      showSuccessMessage('User registered successfully');
      sessionStorage.setItem('userRole', userRole);
      sessionStorage.removeItem('tenantId');

      setTimeout(async () => {
        await signOut(auth);
        sessionStorage.removeItem('userRole');
        navigate('/login');
      }, 1500);

    } catch (error) {
      console.error('Error signing in with Google:', error);
      showErrorMessage('An error occurred while signing in.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Decorative circles */}
      <div style={{ ...styles.circle, width: 340, height: 340, top: -80, right: -60 }}></div>
      <div style={{ ...styles.circle, width: 160, height: 160, bottom: 60, left: 40 }}></div>

      <div style={styles.card}>
        <div style={styles.kicker}>CREATE ACCOUNT</div>
        <h1 style={styles.heading}>Join <span style={styles.gold}>Aalay.</span></h1>
        <p style={styles.subtitle}>
          Create your free account and start tracking warranties, contracts, and assets in minutes.
        </p>

        <div style={styles.recaptchaWrap}>
          <ReCaptcha onVerify={handleReCaptchaVerify} />
        </div>

        <button
          style={{
            ...styles.googleBtn,
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
          }}
          onClick={SignInWithGoogle}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span style={styles.spinner}></span>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.41l3.56-2.77.01-.55z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Sign up with Google
            </>
          )}
        </button>

        {successMessage && (
          <div style={styles.successAlert}>{successMessage}</div>
        )}

        {errorMessage && (
          <div style={styles.errorAlert}>{errorMessage}</div>
        )}

        <div style={styles.loginLink}>
          Already have an account?{' '}
          <span style={styles.loginLinkAction} onClick={() => navigate('/login')}>
            Sign in
          </span>
        </div>
      </div>

      <style>{`
        @keyframes nuspin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

/* ── inline styles matching the landing page theme ── */
const styles = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#fefce8',
    fontFamily: "'Epilogue', sans-serif",
    position: 'relative',
    overflow: 'hidden',
    padding: '24px',
  },
  circle: {
    position: 'absolute',
    borderRadius: '50%',
    background: '#fde047',
    opacity: 0.25,
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: '#ffffff',
    border: '1px solid rgba(240, 208, 96, 0.3)',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '460px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(28, 20, 0, 0.06)',
  },
  kicker: {
    display: 'inline-block',
    background: '#fde047',
    color: '#1c1400',
    padding: '6px 16px',
    borderRadius: '60px',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: '18px',
    fontFamily: "'Epilogue', sans-serif",
  },
  heading: {
    fontFamily: "'Unbounded', cursive",
    fontWeight: 900,
    fontSize: '2rem',
    color: '#1c1400',
    margin: '0 0 12px',
    lineHeight: 1.2,
  },
  gold: {
    color: '#f59e0b',
  },
  subtitle: {
    color: '#5a5200',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginBottom: '32px',
  },
  recaptchaWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px',
    border: '1.5px solid #f0d060',
    borderRadius: '10px',
    background: '#ffffff',
    fontFamily: "'Epilogue', sans-serif",
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#1c1400',
    cursor: 'pointer',
    transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  spinner: {
    display: 'inline-block',
    width: '22px',
    height: '22px',
    border: '3px solid rgba(28, 20, 0, 0.15)',
    borderTopColor: '#1c1400',
    borderRadius: '50%',
    animation: 'nuspin 0.7s linear infinite',
  },
  successAlert: {
    marginTop: '16px',
    padding: '12px 16px',
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '10px',
    fontSize: '0.88rem',
    fontWeight: 600,
  },
  errorAlert: {
    marginTop: '16px',
    padding: '12px 16px',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    fontSize: '0.88rem',
    fontWeight: 500,
  },
  loginLink: {
    marginTop: '24px',
    fontSize: '0.88rem',
    color: '#6a6000',
  },
  loginLinkAction: {
    color: '#f59e0b',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
};

export default NewUserSignIn;
